import React from 'react';
import { Car, Navigation, Plus, Minus } from 'lucide-react';
import { Driver, DriverStatus } from '../../types/fleet';
import { statusLabels } from '../../utils/format';
import { cn } from '../../utils/cn';
const pinColors: Record<DriverStatus, string> = {
  free: 'bg-emerald-500 ring-emerald-500/25',
  busy: 'bg-amber-500 ring-amber-500/25',
  offline: 'bg-red-500 ring-red-500/25'
};
interface FleetMapProps {
  drivers: Driver[];
  selectedId?: string | null;
  onSelect?: (id: string) => void;
  className?: string;
}
export function FleetMap({
  drivers,
  selectedId,
  onSelect,
  className
}: FleetMapProps) {
  return <div className={cn('relative h-full w-full overflow-hidden rounded-xl border border-border bg-[#eef2f6]', className)} role="application" aria-label="Карта водителей" data-id="element-124" data-mp="25rfi7lnux892">
      {/* Схематичная карта города */}
      <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true" data-id="element-125" data-mp="fdygw5gslqvn">
        <rect width="100" height="100" fill="#eef2f6" data-id="element-126" data-mp="1s8gt19nnnjen" />
        {/* Парки */}
        <rect x="4" y="6" width="18" height="14" rx="2" fill="#dcefe0" data-id="element-127" data-mp="1nhs3hzvjcwi5" />
        <rect x="74" y="66" width="22" height="20" rx="2" fill="#dcefe0" data-id="element-128" data-mp="1nffbsuzz77rw" />
        {/* Река */}
        <path d="M-2 78 C 20 66, 34 92, 56 80 C 74 70, 86 92, 104 84 L 104 104 L -2 104 Z" fill="#cfe1f2" data-id="element-129" data-mp="1igb3e6kvle2m" />
        {/* Кварталы */}
        {[12, 30, 48, 66, 84].map(x => [10, 28, 46, 64].map(y => <rect key={`${x}-${y}`} x={x} y={y} width="12" height="9" rx="1" fill="#e3e9ef" data-id="element-130" data-mp="26800e08n6qpw" />))}
        {/* Дороги */}
        {[8, 26, 44, 62, 80].map(y => <line key={`h${y}`} x1="0" y1={y} x2="100" y2={y} stroke="#ffffff" strokeWidth="2.4" data-id="element-131" data-mp="21azsrkw59fwo" />)}
        {[10, 28, 46, 64, 82].map(x => <line key={`v${x}`} x1={x} y1="0" x2={x} y2="100" stroke="#ffffff" strokeWidth="2.4" data-id="element-132" data-mp="pu00uqdpl336" />)}
        {/* Магистраль */}
        <path d="M0 92 C 26 74, 48 96, 100 62" stroke="#ffffff" strokeWidth="3.6" fill="none" data-id="element-133" data-mp="ktc19gbezdxc" />
        <circle cx="50" cy="46" r="24" fill="none" stroke="#ffffff" strokeWidth="2.4" data-id="element-134" data-mp="391dlnw58ia51" />
      </svg>

      {/* Пины водителей */}
      {drivers.map(driver => {
      const active = selectedId === driver.id;
      return <button key={driver.id} type="button" onClick={() => onSelect?.(driver.id)} style={{
        left: `${driver.x}%`,
        top: `${driver.y}%`
      }} className="group absolute z-10 -translate-x-1/2 -translate-y-1/2 focus:outline-none" aria-label={`${driver.name} — ${statusLabels[driver.status]}`} data-id="element-135" data-mp="341ivjuta9ol1">
            <span className={cn('flex h-8 w-8 items-center justify-center rounded-full text-white shadow-md ring-4 transition-transform duration-150 group-hover:scale-110 group-focus-visible:scale-110', pinColors[driver.status], active && 'scale-125 ring-8')} data-id="element-136" data-mp="2gdrigozx57ip">
              <Car className="h-4 w-4" aria-hidden="true" data-id="element-137" data-mp="1rqa23kun46f7" />
            </span>
            {driver.status === 'free' && <span className="pointer-events-none absolute inset-0 animate-ping rounded-full bg-emerald-500/30" data-id="element-138" data-mp="1nqrrpj0g3dqm" />}
            <span className={cn('pointer-events-none absolute left-1/2 top-full z-20 mt-2 w-max -translate-x-1/2 rounded-lg border border-border bg-popover px-2.5 py-1.5 text-left shadow-lg transition-opacity duration-150', active ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100')} data-id="element-139" data-mp="21n50m3qwy6th">
              <span className="block text-xs font-medium text-popover-foreground" data-id="element-140" data-mp="2khtpzi26m8nj">{driver.name}</span>
              <span className="block text-[11px] text-muted-foreground" data-id="element-141" data-mp="2fiphktn30ey9">
                {statusLabels[driver.status]} · {driver.location}
              </span>
            </span>
          </button>;
    })}

      {/* Легенда */}
      <div className="absolute bottom-3 left-3 z-20 rounded-lg border border-border bg-card/95 px-3 py-2 shadow-sm backdrop-blur" data-id="element-142" data-mp="msolbak4p8ap">
        <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground" data-id="element-143" data-mp="rpx3hb5a1cby">Статусы</p>
        <ul className="space-y-1" data-id="element-144" data-mp="wighg97vm9ha">
          {(['free', 'busy', 'offline'] as DriverStatus[]).map(s => <li key={s} className="flex items-center gap-2 text-xs text-foreground" data-id="element-145" data-mp="11g9kwuhsopdv">
              <span className={cn('h-2.5 w-2.5 rounded-full', pinColors[s].split(' ')[0])} data-id="element-146" data-mp="3uu4wbgo7uia" />
              {statusLabels[s]}
            </li>)}
        </ul>
      </div>

      {/* Контролы карты */}
      <div className="absolute right-3 top-3 z-20 flex flex-col overflow-hidden rounded-lg border border-border bg-card shadow-sm" data-id="element-147" data-mp="dq5j8so62zpi">
        <button type="button" className="p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground" aria-label="Приблизить" data-id="element-148" data-mp="1p6m2kx2ue0hn">
          <Plus className="h-4 w-4" data-id="element-149" data-mp="1u4ds5kg0laio" />
        </button>
        <span className="h-px bg-border" data-id="element-150" data-mp="3enhnwun5gkaf" />
        <button type="button" className="p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground" aria-label="Отдалить" data-id="element-151" data-mp="3emg15swru9cv">
          <Minus className="h-4 w-4" data-id="element-152" data-mp="34xy2bh81ycbv" />
        </button>
        <span className="h-px bg-border" data-id="element-153" data-mp="34wd8xuxh4zfl" />
        <button type="button" className="p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground" aria-label="Моё местоположение" data-id="element-154" data-mp="2zyuvpscfby1s">
          <Navigation className="h-4 w-4" data-id="element-155" data-mp="2v1bkl1b0w1e2" />
        </button>
      </div>
    </div>;
}